import { useCallback, useRef } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { ProjectId, Team } from 'api/models';
import { Action, EventType, ObjectType } from 'api/pushes';
import { TeamSocketData } from 'api/pushes/models';
import {
  FetchableResourceType,
  ResourceType,
  RoleIdentifier,
  TeamReplacementSocketData,
  TeamWithAdditionalInfo,
  UserIdentifier,
} from 'api/resource-management/types';
import {
  TeamReplacementProps,
  TeamReplacementWithCompanyProps,
} from 'api/teams';
import { GroupBy } from 'components/organisms/resource-management/types';
import { INVALID_ROLE_ID } from 'components/organisms/resource-management/utils/common';
import {
  resourceIdForTeamAsLeaf,
  resourceIdForExpandableRole,
  resourceIdForCompany,
} from 'components/organisms/resource-management/utils/id';
import { expandableRoleAsResource } from 'components/organisms/resource-management/utils/resource-tree';
import { useAccountWsUpdates } from 'hooks/use-account-ws-updates';
import {
  PlaceholderResolutionsByProject,
  PlaceholderResolutionsQueryKey,
  getTeamsInAccountQueryKey,
  useResolveTeamInLocalState,
} from 'queries/placeholder';
import { useAddProjectUserLiteToQueryState } from 'queries/project';
import { logger } from 'services';
import { updateProjectPlaceholder } from 'stores/memberships';
import { useAggregationManager } from '../../aggregation-manager';
import { useAddRemoveResource } from '../../resource-manager';
import { useAllocationAndSummaryManager } from '../../resource-summary-manager';
import { ManagerPropsWithLoadedRange } from '../../types';
import { useTotalAllocationInUIManager } from '../../ui-total-allocation-manager';

export const useTeamsWSManager = (props: ManagerPropsWithLoadedRange): void => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const {
    schedulerRef,
    userLookup,
    teamLookup,
    roleLookup,
    rangeLoadedInUI,
    groupBy,
  } = props;

  const { updateAllocationAndSummary } = useAllocationAndSummaryManager(props);
  const { addResource, removeResource } = useAddRemoveResource(props);
  const { removeAggregationForResource, getAllocatedMinutesFromAggregation } =
    useAggregationManager();
  const { setTotalAllocationForResource } =
    useTotalAllocationInUIManager(props);
  const addProjectUserLiteToQueryState = useAddProjectUserLiteToQueryState();
  const resolveTeamInLocalState = useResolveTeamInLocalState();

  const moveTeamResourceUnderPartnerCompany = useCallback(
    ({
      projectId,
      teamId,
      companyId,
      roleId,
    }: TeamReplacementWithCompanyProps) => {
      if (groupBy !== GroupBy.Users) {
        return;
      }
      schedulerRef.current.instance.suspendRefresh();
      const existingTeamResource =
        schedulerRef.current.instance.resourceStore.getById(
          resourceIdForTeamAsLeaf(projectId, teamId),
        );

      if (existingTeamResource == null) {
        return;
      }
      const toRoleResource =
        schedulerRef.current.instance.resourceStore.getById(
          resourceIdForExpandableRole(roleId, companyId),
        );

      if (toRoleResource == null) {
        const companyResource =
          schedulerRef.current.instance.resourceStore.getById(
            resourceIdForCompany(companyId),
          );
        const role = roleLookup[roleId];

        if (companyResource == null || role == null) {
          return;
        }
        const newExpandableRoleResource = expandableRoleAsResource(role, {
          [roleId]: [],
        });
        const addedResource = companyResource.appendChild(
          newExpandableRoleResource,
        );

        if (addedResource == null) {
          return;
        }
        addedResource.appendChild(existingTeamResource);
      } else {
        toRoleResource.appendChild(existingTeamResource);
      }

      updateAllocationAndSummary(
        {
          [FetchableResourceType.RoleList]: [
            teamLookup[teamId].roleId ?? INVALID_ROLE_ID,
          ],
        },
        rangeLoadedInUI,
      );

      schedulerRef.current.instance.resumeRefresh(true);
    },
    [
      groupBy,
      rangeLoadedInUI,
      roleLookup,
      schedulerRef,
      teamLookup,
      updateAllocationAndSummary,
    ],
  );
  const moveTeamResourceFromPartnerCompany = useCallback(
    ({ projectId, teamId, roleId }: TeamReplacementWithCompanyProps) => {
      if (groupBy !== GroupBy.Users) {
        return;
      }
      schedulerRef.current.instance.suspendRefresh();
      const existingTeamResource =
        schedulerRef.current.instance.resourceStore.getById(
          resourceIdForTeamAsLeaf(projectId, teamId),
        );

      if (existingTeamResource == null) {
        return;
      }
      const toRoleResource =
        schedulerRef.current.instance.resourceStore.getById(
          resourceIdForExpandableRole(roleId),
        );

      if (toRoleResource == null) {
        const role = roleLookup[roleId];

        if (role == null) {
          return;
        }
        const newExpandableRoleResource = expandableRoleAsResource(role, {
          [roleId]: [],
        });
        const addedResource =
          schedulerRef.current.instance.resourceStore.insert(
            0,
            newExpandableRoleResource,
          );

        if (addedResource == null) {
          return;
        }
        addedResource.appendChild(existingTeamResource);
      } else {
        toRoleResource.appendChild(existingTeamResource);
      }

      updateAllocationAndSummary(
        {
          [FetchableResourceType.RoleList]: [
            teamLookup[teamId].roleId ?? INVALID_ROLE_ID,
          ],
        },
        rangeLoadedInUI,
      );

      schedulerRef.current.instance.resumeRefresh(true);
    },
    [
      groupBy,
      rangeLoadedInUI,
      roleLookup,
      schedulerRef,
      teamLookup,
      updateAllocationAndSummary,
    ],
  );

  const reassignAllocations = useCallback(
    ({ projectId, teamId, userId, roleId }: TeamReplacementProps) => {
      schedulerRef.current.instance.suspendRefresh();

      addResource({
        projectId,
        resource: {
          type: ResourceType.User,
          user: userLookup[userId],
          role: roleLookup[roleId],
          referenceTeam: teamLookup[teamId],
        },
      });

      const replacedUser: UserIdentifier = {
        type: ResourceType.User,
        userId,
        roleId,
        referenceTeamId: teamId,
      };

      const team: RoleIdentifier = {
        type: ResourceType.Role,
        teamId,
        roleId: teamLookup[teamId].roleId ?? INVALID_ROLE_ID,
      };

      updateAllocationAndSummary(
        {
          [FetchableResourceType.UserList]: [userId],
          [FetchableResourceType.RoleList]: [
            teamLookup[teamId].roleId ?? INVALID_ROLE_ID,
          ],
        },
        rangeLoadedInUI,
      ).then(() => {
        setTotalAllocationForResource(
          projectId,
          replacedUser,
          getAllocatedMinutesFromAggregation(projectId, replacedUser)
            .totalAllocatedMinutes,
        );

        removeResource(projectId, {
          type: ResourceType.Team,
          team: teamLookup[teamId], // TODO: Check if lookups get updated or not
        });

        removeAggregationForResource(team, projectId);
        resolveTeamInLocalState(projectId, teamId, userId);
      });

      schedulerRef.current.instance.resumeRefresh(true);
    },
    [
      addResource,
      getAllocatedMinutesFromAggregation,
      rangeLoadedInUI,
      removeAggregationForResource,
      removeResource,
      resolveTeamInLocalState,
      roleLookup,
      schedulerRef,
      setTotalAllocationForResource,
      teamLookup,
      updateAllocationAndSummary,
      userLookup,
    ],
  );

  const handleTeamUpdate = useCallback(
    (
      socketData: TeamSocketData<
        TeamWithAdditionalInfo | TeamReplacementSocketData
      >,
    ) => {
      logger.log('team-update ~ socketData', socketData);

      try {
        const { data } = socketData;

        if (data.userList != null && data.userList.length > 0) {
          const { projectId } = socketData;
          const team = data as TeamReplacementSocketData;
          const user = team.userList?.[0];

          if (projectId && user != null) {
            const { userId } = user;
            const roleId = teamLookup[team.teamId].roleId ?? INVALID_ROLE_ID;

            addProjectUserLiteToQueryState(projectId, {
              user: { userId },
              role: roleLookup[roleId],
              referenceTeam: teamLookup[team.teamId],
            });
            reassignAllocations({
              projectId,
              userId,
              roleId,
              teamId: team.teamId,
            });
          }
        } else if (data.companyList != null && data.companyList.length > 0) {
          const { projectId } = socketData;
          const team = data as TeamReplacementSocketData;
          const company = team.companyList?.[0];

          if (projectId && company != null) {
            const { companyId } = company;
            const roleId = teamLookup[team.teamId].roleId ?? INVALID_ROLE_ID;

            moveTeamResourceUnderPartnerCompany({
              projectId,
              teamId: team.teamId,
              companyId,
              roleId,
            });
            queryClient.invalidateQueries(PlaceholderResolutionsQueryKey);
          }
        } else {
          const { projectId } = data as TeamWithAdditionalInfo;

          if (projectId != null) {
            const resource =
              schedulerRef.current.instance.resourceStore.getById(
                resourceIdForTeamAsLeaf(projectId, data.teamId),
              );

            if (resource == null) {
              return;
            }
            const resolvedWith =
              queryClient.getQueryData<PlaceholderResolutionsByProject>(
                PlaceholderResolutionsQueryKey,
              )?.[projectId]?.mappings[data.teamId];

            if (
              resolvedWith?.identityType === 'COMPANY' &&
              (data.companyList == null || data.companyList?.length === 0)
            ) {
              moveTeamResourceFromPartnerCompany({
                projectId,
                teamId: data.teamId,
                roleId: teamLookup[data.teamId].roleId ?? INVALID_ROLE_ID,
                companyId: resolvedWith.companyId,
              });
              queryClient.invalidateQueries(PlaceholderResolutionsQueryKey);
            } else {
              queryClient.setQueryData<Team[]>(
                getTeamsInAccountQueryKey(),
                (oldValues = []) => [
                  ...oldValues.filter(({ teamId }) => teamId !== data.teamId),
                  data as Team,
                ],
              );
              dispatch(
                updateProjectPlaceholder({
                  projectId: ProjectId(projectId),
                  team: data as Team,
                }),
              );
              resource.set('teamName', (data as Team).teamName);
            }
          }
        }
      } catch (error) {
        logger.error('Error while updating team', { error });
      }
    },
    [
      addProjectUserLiteToQueryState,
      dispatch,
      moveTeamResourceFromPartnerCompany,
      moveTeamResourceUnderPartnerCompany,
      queryClient,
      reassignAllocations,
      roleLookup,
      schedulerRef,
      teamLookup,
    ],
  );

  const handlerRef = useRef<any>();

  handlerRef.current = handleTeamUpdate;

  const handlerProxy = useCallback((params) => {
    handlerRef.current?.(params);
  }, []);

  useAccountWsUpdates(handlerProxy, EventType(Action.Update, ObjectType.Team));
};
